import { computed, createApp, defineComponent, onBeforeUnmount, onMounted, ref } from "vue/dist/vue.esm-bundler.js";
import { listMissingAssistantBridgeMethods } from "./bridge.js";
import { AssistantHeader } from "./components/AssistantHeader.js";
import { ChatComposer } from "./components/ChatComposer.js";
import { MessageList } from "./components/MessageList.js";
import { TaskHistoryPanel } from "./components/TaskHistoryPanel.js";

const AssistantApp = defineComponent({
    name: "AssistantApp",
    props: {
        bridge: { type: Object, required: true }
    },
    components: {
        AssistantHeader,
        ChatComposer,
        MessageList,
        TaskHistoryPanel
    },
    setup(props) {
        const version = ref(0);
        const composerText = ref("");
        const view = computed(() => {
            version.value;
            return props.bridge.getViewModel();
        });

        let unsubscribe = () => {};
        onMounted(() => {
            unsubscribe = props.bridge.subscribe(() => {
                version.value += 1;
            });
        });
        onBeforeUnmount(() => {
            unsubscribe();
        });

        async function submitComposer() {
            const ok = await props.bridge.submitComposer(composerText.value);
            if (ok) composerText.value = "";
        }

        async function onQuickAction(mode) {
            if (view.value.busy) return;
            const payload = mode === "summary" || mode === "review" || mode === "st" ? "" : composerText.value;
            await props.bridge.runAction(mode, payload);
            if (mode !== "summary" && mode !== "review" && mode !== "st") {
                composerText.value = "";
            }
        }

        async function onAction(action) {
            await props.bridge.handleAction(action);
        }

        async function submitQuestion(questionId) {
            await props.bridge.submitQuestionDraft(questionId, view.value.questionDraftInput);
        }

        return {
            composerText,
            view,
            submitComposer,
            onQuickAction,
            onAction,
            submitQuestion
        };
    },
    template: `
        <div class="chat-shell" :class="{ 'is-hidden': !view.uiOpen }">
            <div class="chat-window">
                <AssistantHeader :bridge="bridge" :view="view" />
                <TaskHistoryPanel :bridge="bridge" :view="view" />
                <MessageList :bridge="bridge" :view="view" :on-action="onAction" :submit-question="submitQuestion" />
                <ChatComposer
                    :bridge="bridge"
                    :view="view"
                    :composer-text="composerText"
                    :submit-composer="submitComposer"
                    :on-quick-action="onQuickAction"
                    @update:composer-text="composerText = $event"
                />
            </div>
        </div>
    `
});

export function mountAssistantApp(target, bridge) {
    if (!target || !bridge) return null;
    const missing = listMissingAssistantBridgeMethods(bridge);
    if (missing.length > 0) {
        throw new Error(`Invalid assistant bridge: missing ${missing.join(", ")}`);
    }
    const app = createApp(AssistantApp, { bridge });
    app.mount(target);
    return app;
}
